<script lang="ts">
  import { onMount } from 'svelte';
  import { dndzone, SHADOW_ITEM_MARKER_PROPERTY_NAME } from 'svelte-dnd-action';
  import { api } from '../lib/api';
  import { session } from '../lib/session.svelte';
  import { decryptPrivateCleartext } from '../lib/privateCleartext';
  import ActivityForm from './ActivityForm.svelte';
  import ActivityRow from './ActivityRow.svelte';
  import type { Activity } from '../../../shared/types';

  interface Props {
    /** When true, render only public+semi (the "/" landing). When false, show
     *  the full authenticated dashboard including the viewer's own private rows. */
    publicOnly?: boolean;
    /** Monotonic counter from App. Changes (relative to the value at mount)
     *  trigger a re-fetch. Lets the wordmark / "Min liste" buttons refresh
     *  this view even when the user is already on /hjem or /. */
    reloadKey?: number;
  }
  let { publicOnly = false, reloadKey = 0 }: Props = $props();

  let activities: Activity[] = $state([]);
  let loading = $state(true);
  let showForm = $state(false);
  let editing: Activity | null = $state(null);
  let error: string | null = $state(null);
  let query = $state('');

  // Two toggles control what shape of list we ask the server for:
  //   - default: active rows only (excludes archived AND hidden)
  //   - showArchived: include archived rows mixed in with active
  //   - showHidden: include hidden rows mixed in
  // The user can flip these independently. Anonymous (publicOnly) viewers
  // never see the toggles since they have no archive/hide state.
  let showArchived = $state(false);
  let showHidden = $state(false);

  // Track the reloadKey we've already reacted to so the $effect below
  // only triggers when the prop CHANGES — not on first paint, where
  // onMount already runs the initial load. load() doesn't touch reloadKey
  // so this can't self-fire.
  let lastSeenReloadKey = reloadKey;
  onMount(() => load());

  $effect(() => {
    if (reloadKey !== lastSeenReloadKey) {
      lastSeenReloadKey = reloadKey;
      load();
    }
  });

  async function load() {
    loading = true;
    try {
      activities = await api.listActivities(
        publicOnly
          ? undefined
          : {
              ...(showArchived ? { archived: '1' as const } : {}),
              ...(showHidden ? { hidden: '1' as const } : {}),
            },
      );
    } catch (e) {
      error = 'Kunne ikke laste oppføringer.';
    } finally {
      loading = false;
    }
  }

  // Re-fetch from the server when the toggles flip — the WHERE clause is
  // server-side so we can't filter the existing array client-side. Triggered
  // from the checkbox onchange handlers below, NOT from a $effect — the
  // effect form had a self-fire loop (each load() toggles `loading`, the
  // effect tracked `loading`, every cycle re-triggered itself, list stayed
  // stuck on "Laster …").
  function onToggleArchive(e: Event) {
    showArchived = (e.currentTarget as HTMLInputElement).checked;
    load();
  }
  function onToggleHidden(e: Event) {
    showHidden = (e.currentTarget as HTMLInputElement).checked;
    load();
  }

  function onCreated(a: Activity) {
    activities = [a, ...activities];
    showForm = false;
  }

  function onUpdated(a: Activity) {
    activities = activities.map((x) => (x.id === a.id ? a : x));
    editing = null;
  }

  function onChanged(a: Activity) {
    // When a row's archived/hidden state flips, it may no longer belong in
    // the current view (toggles are off → archived/hidden rows disappear).
    // Reload to get the authoritative list from the server.
    const needsRefetch =
      (a.viewer_archived && !showArchived) || (a.viewer_hidden && !showHidden);
    activities = activities.map((x) => (x.id === a.id ? a : x));
    if (needsRefetch) load();
  }

  function onDeleted(id: string) {
    activities = activities.filter((a) => a.id !== id);
  }

  /** Pre-decrypt private payloads once per render so search can match. */
  const privateCleartext = $derived(decryptPrivateCleartext(activities, session.dek));

  function matchesQuery(a: Activity, q: string): boolean {
    if (!q) return true;
    const needle = q.toLowerCase();
    if (a.visibility === 'private') {
      const p = privateCleartext.get(a.id);
      if (!p) return false;
      return [p.title, p.description ?? '', p.loc_label ?? '', ...p.tags]
        .some((s) => s.toLowerCase().includes(needle));
    }
    return [
      a.title,
      a.description ?? '',
      a.loc_label ?? '',
      ...a.tags,
      a.visibility === 'public' ? a.owner_display ?? '' : '',
    ].some((s) => s.toLowerCase().includes(needle));
  }

  // The list is unified — one column. On the public landing ("/", which
  // anonymous and logged-in users both see), the order is strictly
  // newest-first regardless of any personal sort the viewer may have
  // applied on /hjem. On the authenticated dashboard, sort by the
  // viewer's effective sort_position (custom if dragged, else
  // -created_at, both surfaced by the server).
  const filtered = $derived(
    activities
      .filter((a) => !publicOnly || a.visibility !== 'private')
      .filter((a) => matchesQuery(a, query))
      .slice()
      .sort((a, b) =>
        publicOnly
          ? b.created_at - a.created_at
          : a.sort_position - b.sort_position,
      ),
  );

  // --- Drag-and-drop reordering --------------------------------------------
  // svelte-dnd-action handles touch, mouse, AND keyboard reorder uniformly.
  // We hand it the `filtered` array and let it dispatch `consider`/`finalize`
  // CustomEvents as the user drags.
  //
  // dragDisabled is a pure function of auth state — NOT a handle toggle.
  // An earlier version gated it on handle pointerdown, which had two bugs:
  //   1. Desktop required two gestures (handle to "enable", then card to
  //      drag) because pointerdown flipped the flag too late for the
  //      already-in-progress mouse gesture.
  //   2. Keyboard reorder relies on dragDisabled being false at focus time
  //      — gating it broke Space/arrow-key lifting.
  // The library's built-in distance threshold prevents accidental drags
  // from clicks. Form controls and links inside cards aren't draggable.
  // The drag-handle icon stays as a visual affordance only.

  /** Items the library mutates during the drag. Re-synced from `filtered`
   *  between drags via the $effect below. */
  let dndItems: Activity[] = $state([]);
  /** Drag is disabled for anonymous viewers and on the public landing. */
  const dragDisabled = $derived(!session.user || publicOnly);

  /** Set to true while a drag is in progress, so the resync effect doesn't
   *  fight the library by overwriting its in-flight item ordering. */
  let dragActive = $state(false);

  $effect(() => {
    if (!dragActive) dndItems = filtered.slice();
  });

  function handleConsider(e: CustomEvent<{ items: Activity[] }>) {
    // Library asks us to render this transient ordering during the drag.
    dragActive = true;
    dndItems = e.detail.items;
  }

  async function handleFinalize(e: CustomEvent<{ items: Activity[]; info: { id: string } }>) {
    const next = e.detail.items;
    const movedId = e.detail.info.id;
    dndItems = next;
    dragActive = false;

    // Find where the moved item landed and compute a midpoint position
    // between its (new) neighbours' sort_positions.
    const idx = next.findIndex((a) => a.id === movedId);
    if (idx < 0) return;
    const moved = next[idx]!;
    const prev = idx > 0 ? next[idx - 1] : null;
    const nextItem = idx < next.length - 1 ? next[idx + 1] : null;

    const newPos =
      prev && nextItem ? (prev.sort_position + nextItem.sort_position) / 2 :
      prev ? prev.sort_position + 1.0 :
      nextItem ? nextItem.sort_position - 1.0 :
      0;

    // Optimistic update on the source-of-truth `activities` array so the
    // visual ordering doesn't snap back when the effect above re-syncs.
    const before = activities;
    activities = activities.map((a) =>
      a.id === movedId ? ({ ...a, sort_position: newPos } as Activity) : a,
    );
    try {
      await api.sortActivity(movedId, newPos);
    } catch {
      activities = before;
      error = 'Kunne ikke endre rekkefølge.';
    }
  }
</script>

<section aria-label="Aktiviteter">
  <div class="row" style="justify-content: space-between; margin-bottom: 1rem;">
    {#if publicOnly}
      <div class="landing-hero" style="flex: 1 1 100%;">
        <p style="margin: 0 0 0.5rem;">
          Her kan du lage og dele lister over ting å gjøre om vinteren for å
          holde vinterdepresjonen unna. Og selvfølgelig kan du også lage
          lister for andre formål — noen liker jo ikke sommeren heller.
        </p>
        <p class="muted-secondary" style="margin: 0;">
          Du velger selv om hver oppføring er privat (kryptert i nettleseren
          din), anonym (synlig uten navn), eller offentlig. Du kan også dele
          ting med vennene dine.
        </p>
      </div>
    {:else if session.user}
      <p class="muted" style="margin: 0;">
        {#if session.user.display_name?.trim()}
          Velkommen, {session.user.display_name}. Her er aktivitetene dine for vinteren.
        {:else}
          Her er aktivitetene dine for vinteren.
        {/if}
      </p>
      {#if !showForm && !editing}
        <button class="primary" onclick={() => (showForm = true)}>Ny aktivitet</button>
      {/if}
    {/if}
  </div>

  <label for="search" class="muted" style="display: block; margin-bottom: 0.25rem;">Søk</label>
  <input
    id="search"
    type="search"
    placeholder="Søk i titler, etiketter, sted, forfatter …"
    bind:value={query}
    aria-label="Søk i aktiviteter"
  />

  {#if !publicOnly && session.user}
    <div class="row" style="gap: 1rem; margin-top: 0.5rem; font-size: 0.9rem;">
      <label class="row" style="gap: 0.35rem;">
        <input type="checkbox" checked={showArchived} onchange={onToggleArchive} />
        <span class="muted">📦 Vis arkivert</span>
      </label>
      <label class="row" style="gap: 0.35rem;">
        <input type="checkbox" checked={showHidden} onchange={onToggleHidden} />
        <span class="muted">🙈 Vis skjult</span>
      </label>
    </div>
  {/if}

  {#if showForm}
    <div style="margin-top: 1rem;">
      <ActivityForm onCreated={onCreated} onCancel={() => (showForm = false)} />
    </div>
  {/if}

  {#if editing}
    <div style="margin-top: 1rem;">
      <ActivityForm
        existing={editing}
        onCreated={onUpdated}
        onCancel={() => (editing = null)}
      />
    </div>
  {/if}

  {#if loading}
    <p class="muted">Laster …</p>
  {:else if error}
    <p class="error">{error}</p>
  {:else if filtered.length === 0}
    {#if query}
      <p class="muted">Ingen treff på «{query}».</p>
    {:else}
      <p class="muted">
        {publicOnly ? 'Ingen offentlige aktiviteter ennå.' : 'Ingen aktiviteter ennå. Trykk «Ny aktivitet» for å starte.'}
      </p>
    {/if}
  {:else}
    <!--
      svelte-dnd-action's dndzone owns the list while a drag is active.
      We always render via dndItems (kept in sync with `filtered` between
      drags by the $effect above) so the visual stays consistent.
    -->
    <div
      use:dndzone={{
        items: dndItems,
        dragDisabled,
        flipDurationMs: 180,
        // Type "shadow item" is the library's placeholder while dragging;
        // we render it transparently to keep layout stable.
        dropTargetStyle: {},
        type: 'activities',
      }}
      onconsider={handleConsider}
      onfinalize={handleFinalize}
      aria-label="Aktivitetsliste"
    >
      {#each dndItems as a (a.id)}
        <!-- The library tags the shadow item via SHADOW_ITEM_MARKER_PROPERTY_NAME;
             dim it slightly so the drop target stays visible without flashing. -->
        <div style={(a as unknown as Record<string, unknown>)[SHADOW_ITEM_MARKER_PROPERTY_NAME] ? 'opacity: 0.3;' : ''}>
          <ActivityRow
            activity={a}
            privateCleartext={a.visibility === 'private' ? (privateCleartext.get(a.id) ?? null) : null}
            onDeleted={onDeleted}
            onEdit={(act) => (editing = act)}
            onChanged={onChanged}
          />
        </div>
      {/each}
    </div>
  {/if}
</section>
